import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Eye,
  MessageSquare,
  Share2,
  RefreshCw,
  Users,
  TrendingUp,
  Lightbulb,
  Pencil,
} from "lucide-react";
import { renewAd } from "@/lib/ad-stats.functions";
import { AD_LIFETIME_DAYS, buildAdInsights, daysLeft, type AdStatRow } from "@/lib/ad-stats";
import { Button } from "@/components/ui/button";
import { AdDailyStats } from "@/components/AdDailyStats";
import { myAdStatsQuery, myPlanQuery } from "@/lib/queries";
import { PLANS } from "@/lib/plans";

export const Route = createFileRoute("/_authenticated/statistik")({
  head: () => ({
    meta: [
      { title: "Statistik för mina annonser — Sellavo" },
      {
        name: "description",
        content:
          "Se hur många som tittar på dina annonser, hur många som hör av sig och vad du bör justera för att sälja snabbare.",
      },
      { property: "og:title", content: "Statistik för mina annonser — Sellavo" },
      {
        property: "og:description",
        content: "Visningar, kontakter och konkreta tips för dina annonser.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(myAdStatsQuery()),
      context.queryClient.ensureQueryData(myPlanQuery()),
    ]);
  },
  component: StatsPage,
});

const toneClass: Record<string, string> = {
  good: "border-primary/30 bg-primary/5",
  warn: "border-amber-500/40 bg-amber-500/10",
  info: "border-border bg-muted/40",
};

function StatsPage() {
  const doRenew = useServerFn(renewAd);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(myAdStatsQuery());
  const { data: planStatus } = useQuery(myPlanQuery());
  const plan = planStatus?.plan ?? "free";
  const canSeeDaily = plan !== "free";

  const renew = useMutation({
    mutationFn: (slug: string) => doRenew({ data: { slug } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-ad-stats"] });
      queryClient.invalidateQueries({ queryKey: ["my-ads"] });
      queryClient.invalidateQueries({ queryKey: ["ads"] });
    },
  });

  const ads = (data?.ads ?? []) as AdStatRow[];
  const total = ads.reduce(
    (acc, a) => ({
      views: acc.views + a.views,
      contacts: acc.contacts + a.contacts,
      week: acc.week + a.viewsLast7,
    }),
    { views: 0, contacts: 0, week: 0 },
  );

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
            Statistik
          </h1>
          <p className="mt-2 text-muted-foreground">
            Se hur dina annonser går och vad du kan göra för att sälja snabbare.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/mina-annonser">Mina annonser</Link>
        </Button>
      </div>

      {isLoading ? (
        <p className="mt-8 text-muted-foreground">Laddar…</p>
      ) : ads.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-border p-10 text-center">
          <p className="text-muted-foreground">Du har inga annonser ännu.</p>
          <Button asChild className="mt-4">
            <Link to="/">Skapa din första annons</Link>
          </Button>
        </div>
      ) : (
        <>
          {planStatus && (
            <div className="mt-6 flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm">
              <span className="rounded-md bg-primary/10 px-2 py-1 font-semibold text-primary">
                {PLANS[plan].name}
              </span>
              <span className="text-muted-foreground">
                {canSeeDaily
                  ? "Du har detaljerad statistik: öppna en annons nedan för att se besökare dag för dag och vilka dagar frågorna kom in."
                  : "Med Plus eller Pro ser du statistiken dag för dag – besökare per dag och vilka dagar någon hörde av sig."}
              </span>
              {!canSeeDaily && (
                <Link
                  to="/priser"
                  className="ml-auto font-semibold text-primary underline underline-offset-2"
                >
                  Se planer
                </Link>
              )}
            </div>
          )}

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <SummaryCard
              icon={<Eye className="h-4 w-4" />}
              label="Visningar totalt"
              value={total.views}
            />
            <SummaryCard
              icon={<TrendingUp className="h-4 w-4" />}
              label="Senaste 7 dagarna"
              value={total.week}
            />
            <SummaryCard
              icon={<MessageSquare className="h-4 w-4" />}
              label="Kontakter"
              value={total.contacts}
            />
          </div>

          <div className="mt-8 space-y-5">
            {ads.map((ad) => {
              const left = daysLeft(ad.expires_at);
              const pct = Math.max(0, Math.min(100, (left / AD_LIFETIME_DAYS) * 100));
              const insights = buildAdInsights(ad);

              return (
                <section
                  key={ad.slug}
                  className="rounded-lg border border-border bg-card p-5 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <Link
                        to="/a/$slug"
                        params={{ slug: ad.slug }}
                        className="font-heading text-lg font-bold text-foreground hover:underline"
                      >
                        {ad.title}
                      </Link>
                      <p className="text-sm text-muted-foreground">
                        {ad.price.toLocaleString("sv-SE")} kr
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button asChild variant="outline" size="sm">
                        <Link to="/redigera/$slug" params={{ slug: ad.slug }}>
                          <Pencil className="h-4 w-4" /> Redigera
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant={left <= 7 ? "default" : "outline"}
                        disabled={renew.isPending}
                        onClick={() => renew.mutate(ad.slug)}
                        title={`Ge annonsen ${AD_LIFETIME_DAYS} nya dagar`}
                      >
                        <RefreshCw className="h-4 w-4" /> Förnya
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Stat icon={<Eye className="h-4 w-4" />} label="Visningar" value={ad.views} />
                    <Stat
                      icon={<Users className="h-4 w-4" />}
                      label="Unika besökare"
                      value={ad.uniqueViews}
                    />
                    <Stat
                      icon={<MessageSquare className="h-4 w-4" />}
                      label="Kontakter"
                      value={ad.contacts}
                    />
                    <Stat
                      icon={<Share2 className="h-4 w-4" />}
                      label="Delningar"
                      value={ad.shares}
                    />
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {ad.is_paused
                          ? left <= 0
                            ? "Utgången – pausades automatiskt"
                            : "Pausad"
                          : `Aktiv i ${Math.max(left, 0)} dagar till`}
                      </span>
                      <span>{AD_LIFETIME_DAYS} dagars annonstid</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full ${left <= 7 ? "bg-amber-500" : "bg-primary"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>

                  <AdDailyStats slug={ad.slug} canSeeDaily={canSeeDaily} />

                  <div className="mt-4 space-y-2">
                    {insights.map((tip, i) => (
                      <div
                        key={i}
                        className={`rounded-md border p-3 text-sm ${toneClass[tip.tone]}`}
                      >
                        <p className="flex items-center gap-2 font-semibold text-foreground">
                          <Lightbulb className="h-4 w-4 shrink-0 text-primary" /> {tip.title}
                        </p>
                        <p className="mt-1 text-muted-foreground">{tip.body}</p>
                        {tip.suggestedPrice && (
                          <Link
                            to="/redigera/$slug"
                            params={{ slug: ad.slug }}
                            className="mt-2 inline-block font-semibold text-primary underline underline-offset-2"
                          >
                            Justera priset
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </main>
  );
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {icon} {label}
      </p>
      <p className="mt-1 font-heading text-2xl font-bold text-foreground">
        {value.toLocaleString("sv-SE")}
      </p>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon} {label}
      </p>
      <p className="mt-0.5 font-heading text-lg font-bold text-foreground">
        {value.toLocaleString("sv-SE")}
      </p>
    </div>
  );
}
